"use client"

import React, { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  actualTheme: 'dark' | 'light' // The actual computed theme (resolves 'system')
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
  actualTheme: 'dark', // Default to dark since that's our app default
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

// Function to get theme from localStorage (same logic as in layout script)
function getStoredTheme(storageKey: string, defaultTheme: Theme): Theme {
  if (typeof window === 'undefined') return defaultTheme
  
  try {
    const stored = localStorage.getItem(storageKey) as Theme
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error)
  }
  return defaultTheme
}

// Function to compute actual theme (same logic as in layout script)
function computeActualTheme(theme: Theme): 'dark' | 'light' {
  if (typeof window === 'undefined') return 'dark'
  
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return theme === 'dark' ? 'dark' : 'light'
}

export function ThemeProvider({
  children,
  defaultTheme = 'dark',
  storageKey = 'ui-theme',
  ...props
}: ThemeProviderProps) {
  // Initialize with the same logic as the layout script to prevent mismatches
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme(storageKey, defaultTheme))
  const [actualTheme, setActualTheme] = useState<'dark' | 'light'>(() => 
    computeActualTheme(getStoredTheme(storageKey, defaultTheme))
  )
  const [mounted, setMounted] = useState(false)

  // Ensure we're mounted (client-side)
  useEffect(() => {
    setMounted(true)
    
    // Sync with what was already applied by the layout script
    const root = document.documentElement
    const hasLight = root.classList.contains('light')
    const hasDark = root.classList.contains('dark')
    
    if (hasLight) {
      setActualTheme('light')
    } else if (hasDark) {
      setActualTheme('dark')
    }
  }, [])

  useEffect(() => {
    if (!mounted) return
    
    const root = document.documentElement
    root.classList.remove('light', 'dark')

    const computedTheme = computeActualTheme(theme)
    root.classList.add(computedTheme)
    root.style.colorScheme = computedTheme
    setActualTheme(computedTheme)

    // Update meta theme-color
    const themeColorMeta = document.querySelector('meta[name="theme-color"]')
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', computedTheme === 'dark' ? '#000000' : '#ffffff')
    }
  }, [theme, mounted])

  useEffect(() => {
    // Listen for system theme changes when theme is set to 'system'
    if (!mounted || theme !== 'system') return
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e: MediaQueryListEvent) => {
      const computedTheme = e.matches ? 'dark' : 'light'
      const root = document.documentElement
      root.classList.remove('light', 'dark')
      root.classList.add(computedTheme)
      root.style.colorScheme = computedTheme
      setActualTheme(computedTheme)
      
      // Update meta theme-color
      const themeColorMeta = document.querySelector('meta[name="theme-color"]')
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', computedTheme === 'dark' ? '#000000' : '#ffffff')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme, mounted])

  const setTheme = (newTheme: Theme) => {
    try {
      localStorage.setItem(storageKey, newTheme)
      setThemeState(newTheme)
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error)
      setThemeState(newTheme) // Still update state even if localStorage fails
    }
  }

  const value = {
    theme,
    setTheme,
    actualTheme,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}